import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge, InputGroup, Pagination } from 'react-bootstrap'
import { api } from '../../services/api'

interface User {
  id: string
  name: string
  email: string
  role: string
  isActive: boolean
  createdAt: string
  lastLogin?: string
  org?: {
    id: string
    name: string
    slug: string
  }
  _count: {
    ticketsRequested: number
    ticketsAssigned: number
  }
}

interface InviteUserData {
  name: string
  email: string
  password: string
  role: string
  orgId?: string
}

interface Organization {
  id: string
  name: string
  slug: string
}

export const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [inviteData, setInviteData] = useState<InviteUserData>({
    name: '',
    email: '',
    password: '',
    role: 'REQUESTER'
  })
  const [submitting, setSubmitting] = useState(false)
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    const initializePage = async () => {
      await fetchOrganizations()
      // Show all users by default
      await fetchUsers()
    }
    
    initializePage()
  }, [])

  useEffect(() => {
    fetchUsers(selectedOrgFilter || undefined)
  }, [page])

  const fetchUsers = async (orgId?: string) => {
    try {
      setLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (orgId) params.append('orgId', orgId)
      params.append('page', page.toString())
      params.append('limit', itemsPerPage.toString())
      
      const url = `/admin/users?${params.toString()}`
      const response = await api.get(url)
      setUsers(response.data.data || [])
      setTotal(response.data.total || response.data.data?.length || 0)
    } catch (err: any) {
      console.error('Error fetching users:', err)
      setError(err.response?.data?.error?.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/admin/organizations')
      setOrganizations(response.data.data || [])
      return response.data.data
    } catch (err: any) {
      console.error('Error fetching organizations:', err)
      return []
    }
  }

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Frontend validation
    if (inviteData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    
    // Validate organization is selected for non-ADMIN users
    if ((inviteData.role === 'AGENT' || inviteData.role === 'REQUESTER') && !inviteData.orgId) {
      setError('Please select an organization for this user')
      return
    }
    
    setSubmitting(true)
    setError('')
    try {
      await api.post('/admin/users/invite', inviteData)
      
      // Close modal and reset form
      setShowInviteModal(false)
      setInviteData({ name: '', email: '', password: '', role: 'REQUESTER' })
      
      // Refresh user list
      await fetchUsers()
      
      // Show success message
      alert(`✅ User ${inviteData.name} has been created successfully!\n\nEmail: ${inviteData.email}\nRole: ${inviteData.role}`)
    } catch (err: any) {
      console.error('Error creating user:', err)
      console.error('Error response:', err.response?.data)
      
      // Better error handling for validation errors
      const errorMsg = err.response?.data?.error?.message || 
                       err.response?.data?.message || 
                       'Failed to invite user. Please try again.'
      setError(errorMsg)
      
      // Keep modal open so user can see the error and fix it
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/users/${userId}`, { isActive: !isActive })
      fetchUsers()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update user')
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/admin/users/${userId}`)
        fetchUsers()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete user')
      }
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'danger'
      case 'AGENT': return 'warning'
      case 'REQUESTER': return 'info'
      default: return 'secondary'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="users-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Users Management</h2>
          <p className="text-muted mb-0 small">Manage organization users and permissions</p>
        </div>
        <Button variant="primary" onClick={() => setShowInviteModal(true)} className="d-flex align-items-center">
          <i className="fas fa-user-plus me-2"></i>
          Create User
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col xs={12} sm={6} md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{users.length}</h3>
              <p className="text-muted mb-0">Total Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{users.filter(u => u.isActive).length}</h3>
              <p className="text-muted mb-0">Active Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">{users.filter(u => u.role === 'AGENT').length}</h3>
              <p className="text-muted mb-0">Agents</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{users.filter(u => u.role === 'REQUESTER').length}</h3>
              <p className="text-muted mb-0">Requesters</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Organization Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="py-2">
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="fas fa-building me-2 text-primary"></i>
                  Filter by Organization
                </Form.Label>
                <Form.Select
                  value={selectedOrgFilter}
                  onChange={(e) => {
                    const orgId = e.target.value
                    setSelectedOrgFilter(orgId)
                    setPage(1)
                    fetchUsers(orgId || undefined)
                  }}
                >
                  <option value="">All Organizations</option>
                  {organizations.length === 0 && (
                    <option disabled>Loading organizations...</option>
                  )}
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <div className="mt-3 mt-md-0">
                <small className="text-muted d-block">
                  {selectedOrgFilter ? (
                    <>
                      Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, total)} of <strong>{total}</strong> users from <strong>{organizations.find(o => o.id === selectedOrgFilter)?.name || 'selected organization'}</strong>
                    </>
                  ) : (
                    <>
                      Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, total)} of <strong>{total}</strong> users from <strong>all organizations</strong>
                    </>
                  )}
                </small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-users me-2 text-primary"></i>
            Users ({users.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {users.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>User</th>
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Tickets</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{user.name}</div>
                        <small className="text-muted">{user.email}</small>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted small">
                        {user.org?.name || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <Badge bg={getRoleColor(user.role)}>
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={user.isActive ? 'success' : 'danger'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <small className="text-muted">
                          <i className="fas fa-ticket-alt me-1"></i>
                          {user._count.ticketsRequested} requested
                        </small>
                        <small className="text-muted">
                          <i className="fas fa-user-check me-1"></i>
                          {user._count.ticketsAssigned} assigned
                        </small>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted">
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(user.createdAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user)
                            setShowEditModal(true)
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant={user.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                        >
                          <i className={`fas fa-${user.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.id === user?.id}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-users text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No users found</h5>
              <p className="text-muted">Create your first user to get started</p>
              <Button variant="primary" onClick={() => setShowInviteModal(true)}>
                <i className="fas fa-user-plus me-2"></i>Create User
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {Math.ceil(total / itemsPerPage) > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
            
            {[...Array(Math.ceil(total / itemsPerPage))].map((_, i) => {
              const pageNum = i + 1
              const totalPages = Math.ceil(total / itemsPerPage)
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === page}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                )
              } else if (pageNum === page - 2 || pageNum === page + 2) {
                return <Pagination.Ellipsis key={pageNum} disabled />
              }
              return null
            })}
            
            <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === Math.ceil(total / itemsPerPage)} />
            <Pagination.Last onClick={() => setPage(Math.ceil(total / itemsPerPage))} disabled={page === Math.ceil(total / itemsPerPage)} />
          </Pagination>
        </div>
      )}

      {/* Create User Modal */}
      <Modal 
        show={showInviteModal} 
        onHide={() => {
          setShowInviteModal(false)
          setError('') // Clear error when closing
          setInviteData({ name: '', email: '', password: '', role: 'REQUESTER' }) // Reset form
        }} 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
              <strong>Error:</strong> {error}
            </Alert>
          )}
          <Form onSubmit={handleInviteUser}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                value={inviteData.name}
                onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password *</Form.Label>
              <InputGroup>
                <Form.Control
                  type="password"
                  value={inviteData.password}
                  onChange={(e) => setInviteData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  isInvalid={inviteData.password.length > 0 && inviteData.password.length < 8}
                  isValid={inviteData.password.length >= 8}
                />
                <Form.Control.Feedback type="invalid">
                  Password must be at least 8 characters long
                </Form.Control.Feedback>
                <Form.Control.Feedback type="valid">
                  Password meets minimum requirements
                </Form.Control.Feedback>
              </InputGroup>
              <Form.Text className="text-muted">
                Include uppercase, lowercase, numbers, and special characters for better security.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={inviteData.role}
                onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="REQUESTER">Requester</option>
                <option value="AGENT">Agent</option>
                <option value="ADMIN">Admin</option>
              </Form.Select>
            </Form.Group>

            {(inviteData.role === 'AGENT' || inviteData.role === 'REQUESTER') && (
              <Form.Group className="mb-3">
                <Form.Label>Organization *</Form.Label>
                <Form.Select
                  value={inviteData.orgId || ''}
                  onChange={(e) => {
                    console.log('Selected organization ID:', e.target.value)
                    setInviteData(prev => ({ ...prev, orgId: e.target.value }))
                  }}
                  required
                >
                  <option value="">Select an organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {inviteData.role === 'AGENT' ? 'Agents must be assigned to a specific organization' : 'Requesters must be assigned to a specific organization'}
                  {organizations.length === 0 && (
                    <span className="text-danger d-block mt-1">⚠️ No organizations available. Please create an organization first.</span>
                  )}
                  {organizations.length > 0 && (
                    <span className="d-block mt-1">✓ {organizations.length} organization(s) available</span>
                  )}
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleInviteUser}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="fas fa-user-plus me-2"></i>
                Create User
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={selectedUser.name}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  defaultValue={selectedUser.email}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Select defaultValue={selectedUser.role}>
                  <option value="REQUESTER">Requester</option>
                  <option value="AGENT">Agent</option>
                  <option value="ADMIN">Admin</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  label="Active User"
                  defaultChecked={selectedUser.isActive}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary">
            <i className="fas fa-save me-2"></i>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

